import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import {
  HomeIcon,
  FilmIcon,
  MegaphoneIcon,
  ChartBarIcon,
  DocumentTextIcon,
  UserIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  DevicePhoneMobileIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { isDarkMode, toggleTheme } = useThemeStore();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'İçerik', href: '/content', icon: FilmIcon },
    { name: 'İçerik Arama', href: '/content-search', icon: MagnifyingGlassIcon },
    { name: 'Kampanyalar', href: '/campaigns', icon: MegaphoneIcon },
    { name: 'Platformlar', href: '/platforms', icon: DevicePhoneMobileIcon },
    { name: 'Analitik', href: '/analytics', icon: ChartBarIcon, disabled: true },
    ...((user?.role === 'tenant_admin' || user?.role === 'super_admin' || user?.role === 'admin') ? [
      { name: 'Kullanıcılar', href: '/users', icon: UsersIcon }
    ] : []),
    { name: 'Dokümantasyon', href: '/documentation', icon: DocumentTextIcon },
    { name: 'Kullanıcı Ayarları', href: '/user-settings', icon: Cog6ToothIcon },
    ...((user?.isSuperAdmin || user?.role === 'super_admin') ? [
      { name: 'Super Admin', href: '/super-admin', icon: Cog6ToothIcon }
    ] : [])
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transition-colors">
        <div className="flex h-16 items-center justify-center border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Adinscene</h1>
        </div>

        <nav className="mt-8 px-4">
          <ul className="space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isDisabled = item.disabled;

              return (
                <li key={item.name}>
                  {isDisabled ? (
                    <div
                      className="flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-not-allowed opacity-50 text-gray-400 dark:text-gray-500"
                      title="Bu özellik şu anda kullanılamıyor"
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                      }`}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User menu */}
        <div className="bg-white dark:bg-gray-800 absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-gray-700 transition-colors">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="h-8 w-8 text-gray-400 dark:text-gray-300" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 overflow-hidden max-w-[180px]">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100 transition-colors"
              title="Çıkış Yap"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors">
          <div className="flex h-16 items-center justify-between px-6">
            <div className="flex items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {navigation.find(item => item.href === location.pathname)?.name || 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100 transition-colors"
                title={isDarkMode ? 'Gündüz Moduna Geç' : 'Gece Moduna Geç'}
              >
                {isDarkMode ? (
                  <SunIcon className="h-5 w-5" />
                ) : (
                  <MoonIcon className="h-5 w-5" />
                )}
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100">
                <Cog6ToothIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
